// A CSEMPE-REGISZTER: katalógus-id → ikon + renderelés. A KATALÓGUS (kapu, méret, sorrend) a `@atlas/shared`-ben él;
// itt csak a web-specifikus rész van (melyik komponens, melyik ikon). A kapu-döntés NEM itt dől el: a `resolveLayout`
// már csak a jogosult csempéket adja, az adat-lekérdezés pedig a `useDashboardData` kapuján fut.
import {
  DASHBOARD_DEFAULT_WINDOW_DAYS,
  type DashboardWindowDays,
  type ResolvedTile,
} from '@atlas/shared';
import { BuildingOffice2Icon, HomeModernIcon, TicketIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { AttentionCard } from '@/features/dashboard/components/attention-card';
import { CelebrationsCard } from '@/features/dashboard/components/celebrations-card';
import { FinanceCard } from '@/features/dashboard/components/finance-card';
import { KpiCard, MrrKpiCard } from '@/features/dashboard/components/kpi-cards';
import { MyTicketsCard } from '@/features/dashboard/components/my-tickets-card';
import { NotesCard } from '@/features/dashboard/components/notes-card';
import { PinnedMessageCard } from '@/features/dashboard/components/pinned-message-card';
import { UpcomingMeetingsCard } from '@/features/dashboard/components/upcoming-meetings-card';
import type { DashboardData } from '@/features/dashboard/use-dashboard-data';

export interface TileContext {
  data: DashboardData;
  /** Az ügyfél-/fiók-darabszám (`GET /dashboard`) — jog nélkül `null`. */
  counts: { clients: number | null; accounts: number | null };
  countsLoading: boolean;
  currentUserId: string | null;
  /** Az „Ünnepeltek" ablakának mentése (a csempe saját `opts.windowDays`-e). */
  onWindowDaysChange: (days: DashboardWindowDays) => void;
}

/** Egy csempe tartalma — `null`, ha nincs mit mutatni (a sáv `empty:hidden`-nel összehúzódik). */
export function TileView({
  tile,
  ctx,
}: {
  tile: ResolvedTile;
  ctx: TileContext;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { data } = ctx;
  switch (tile.def.id) {
    case 'kpi.clients':
    case 'kpi.accounts': {
      const isClients = tile.def.id === 'kpi.clients';
      if (ctx.countsLoading) return <Skeleton className="h-24 rounded-xl" />;
      const value = isClients ? ctx.counts.clients : ctx.counts.accounts;
      if (value === null) return null;
      return (
        <KpiCard
          to={isClients ? '/clients' : '/accounts'}
          label={t(isClients ? 'dashboard.kpi.clients' : 'dashboard.kpi.accounts')}
          value={value}
          icon={isClients ? BuildingOffice2Icon : HomeModernIcon}
        />
      );
    }
    case 'kpi.openTickets':
      if (data.openTickets === null) return null;
      return (
        <KpiCard
          to="/tickets"
          search={{ group: 'in_progress' }}
          label={t('dashboard.kpi.openTickets')}
          value={data.openTickets}
          icon={TicketIcon}
        />
      );
    case 'kpi.mrr':
      if (data.revenue === null) return null;
      return (
        <MrrKpiCard
          mrr={data.revenue.mrr}
          currency={data.revenue.currency}
          changePct={data.revenue.changePct}
          trend={data.revenue.trend}
        />
      );
    case 'finance':
      return <FinanceCard data={data.finance} loading={data.financeLoading} />;
    case 'meetings':
      return (
        <UpcomingMeetingsCard
          meetings={data.meetings}
          loading={data.meetingsLoading}
          currentUserId={ctx.currentUserId}
        />
      );
    case 'celebrations':
      return (
        <CelebrationsCard
          windowDays={tile.opts?.windowDays ?? DASHBOARD_DEFAULT_WINDOW_DAYS}
          onWindowDaysChange={ctx.onWindowDaysChange}
        />
      );
    case 'pinnedMessages':
      // tétel nélkül nem renderel (a szerkesztőben a keret akkor is látszik — a csempe mozgatható marad)
      if (data.pinnedMessages.length === 0) return null;
      return <PinnedMessageCard messages={data.pinnedMessages} total={data.pinnedTotal} />;
    case 'attention':
      return <AttentionCard data={data.attention} loading={data.attentionLoading} />;
    case 'myTickets':
      return <MyTicketsCard />;
    case 'notes':
      return <NotesCard />;
  }
}
